import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface HospitalSettings {
  id: string;
  hospital_name_en: string;
  hospital_name_bn: string;
  hospital_name_color_en: string;
  hospital_name_color_bn: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  invoice_note: string | null;
  invoice_prefix: string;
  refer_commission_type: 'percentage' | 'amount';
  pathology_room: string;
  usg_room: string;
  xray_room: string;
  mri_room: string;
  ecg_room: string;
  logo_url: string | null;
  report_header_url: string | null;
  report_footer_url: string | null;
  invoice_template: string;
  report_template: string;
  prescription_template: string;
  print_mode: 'pdf_viewer' | 'browser_print';
  watermark_enabled: boolean;
  watermark_left: number;
  watermark_top: number;
  name_size: number;
  subname_size: number;
  invoice_header_gap: number;
  invoice_body_gap: number;
  report_header_gap: number;
  report_body_gap: number;
  report_body_font_size: number;
  report_footer_gap: number;
  report_left_gap: number;
  prescription_header_gap: number;
  prescription_body_gap: number;
  prescription_header_gradient: string | null;
  prescription_header_font_color: string;
  created_at: string;
  updated_at: string;
}

const DEFAULT_SETTINGS: HospitalSettings = {
  id: 'default',
  hospital_name_en: 'Hospital Name',
  hospital_name_bn: 'হাসপাতালের নাম',
  hospital_name_color_en: '#1e40af',
  hospital_name_color_bn: '#1e40af',
  phone: null,
  email: null,
  website: null,
  address: null,
  invoice_note: null,
  invoice_prefix: 'INV',
  refer_commission_type: 'percentage',
  pathology_room: 'Pathology',
  usg_room: 'USG',
  xray_room: 'X-Ray',
  mri_room: 'MRI',
  ecg_room: 'ECG',
  logo_url: null,
  report_header_url: null,
  report_footer_url: null,
  invoice_template: 'a4',
  report_template: 'standard',
  prescription_template: 'template1',
  print_mode: 'pdf_viewer',
  watermark_enabled: false,
  watermark_left: 0,
  watermark_top: 0,
  name_size: 16,
  subname_size: 14,
  invoice_header_gap: 10,
  invoice_body_gap: 10,
  report_header_gap: 10,
  report_body_gap: 10,
  report_body_font_size: 12,
  report_footer_gap: 10,
  report_left_gap: 10,
  prescription_header_gap: 10,
  prescription_body_gap: 10,
  prescription_header_gradient: null,
  prescription_header_font_color: '#ffffff',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export function useSettings() {
  const [settings, setSettings] = useState<HospitalSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        const stored = localStorage.getItem('hospital_settings');
        if (stored) {
          setSettings(JSON.parse(stored));
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('settings')
        .select('*')
        .eq('id', 'default')
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          setSettings(DEFAULT_SETTINGS);
        } else {
          throw fetchError;
        }
      } else {
        setSettings(data as HospitalSettings);
      }
    } catch (err: any) {
      console.error('Error loading settings:', err);
      setError(err.message || 'Failed to load settings');
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates: Partial<HospitalSettings>) => {
    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        const newSettings = { ...settings, ...updates, updated_at: new Date().toISOString() };
        setSettings(newSettings);
        localStorage.setItem('hospital_settings', JSON.stringify(newSettings));
        return { success: true };
      }

      const { error: updateError } = await supabase
        .from('settings')
        .upsert({
          id: 'default',
          ...updates,
        });

      if (updateError) throw updateError;

      await loadSettings();
      return { success: true };
    } catch (err: any) {
      console.error('Error updating settings:', err);
      return { success: false, error: err.message || 'Failed to update settings' };
    }
  };

  const uploadFile = async (file: File, path: string): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        const reader = new FileReader();
        return new Promise((resolve) => {
          reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            resolve({ success: true, url: dataUrl });
          };
          reader.onerror = () => {
            resolve({ success: false, error: 'Failed to read file' });
          };
          reader.readAsDataURL(file);
        });
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${path}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('branding')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('branding')
        .getPublicUrl(filePath);

      return { success: true, url: publicUrl };
    } catch (err: any) {
      console.error('Error uploading file:', err);
      return { success: false, error: err.message || 'Failed to upload file' };
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  return {
    settings,
    loading,
    error,
    updateSettings,
    uploadFile,
    refreshSettings: loadSettings,
  };
}
